import React, { useCallback, useMemo, useRef, useState } from "react";
import _ from "lodash/fp";
import {
  Delete as DeleteIcon,
  Pencil as EditIcon,
  DotsVertical as DotsVerticalIcon,
} from "@actinc/dls/icons/";
import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormContext } from "react-hook-form";

import { SRAccessibilityDialog } from "./SRAccessibilityDialog";

import {
  createElement,
  createSRAccessibilityTag,
  deleteScreenReaderTags,
  getCleanContent,
  getScreenReaderTags,
  getTrimmedRange,
  getSROptions,
  editScreenReaderTagContent,
  expandRangeToFindCommonNode,
} from "../../services/contentUtils";
import { useMemoCompare } from "../../services/hooks";
import {
  getAssetFileName,
  getLetter,
  transformMathMl,
} from "../../services/utils";
import { useClient } from "urql";

const SRAccessibilityPanel = ({ objectType, orientation, readonly }) => {
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState(null);
  const [contextMenuState, setContextMenuState] = useState({
    mouseX: null,
    mouseY: null,
    options: [],
    selection: null,
  });
  const [dialogState, setDialogState] = useState({ open: false });
  const [currentId, setCurrentId] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { getValues, setValue } = useFormContext();
  const client = useClient();

  const [content, srAccessibilityTags] = useMemoCompare(
    getScreenReaderTags(objectType)(getValues(objectType)),
  );
  const interactions = useMemoCompare(
    objectType === "item"
      ? getValues(`item.interactions`).filter(
          ({ __typename }) => __typename === "ChoiceInteraction",
        )
      : [],
  );

  const closeBulkActionsAnchorEl = () => {
    setBulkActionsAnchorEl(null);
  };

  const closeActionsAnchorEl = () => {
    setActionsAnchorEl(null);
  };

  const contentElement = useMemo(() => {
    const element = document.createElement("div");
    element.innerHTML = content;
    return element;
  }, [content]);

  const contentElementRef = useRef();

  const closeContextMenu = () => {
    setContextMenuState({
      mouseX: null,
      mouseY: null,
      options: [],
      selection: null,
    });
  };

  const deleteSelectedSRTags = () => {
    const newObj = deleteScreenReaderTags(`${objectType}`)(
      getValues(`${objectType}`),
    )(selectedTags);
    setValue(`${objectType}`, newObj, { shouldDirty: true });
    setSelectedTags([]);
    closeBulkActionsAnchorEl();
  };

  const deleteSRTag = () => {
    const newObj = deleteScreenReaderTags(`${objectType}`)(
      getValues(`${objectType}`),
    )([currentId]);
    setValue(`${objectType}`, newObj, { shouldDirty: true });
    closeActionsAnchorEl();
    setCurrentId(null);
  };

  const handleSelectAllCheckBox = (e) => {
    if (!selectAll) {
      setSelectedTags(srAccessibilityTags.map((el) => el.identifier));
      setSelectAll(true);
    } else {
      setSelectedTags([]);
      setSelectAll(false);
    }
  };

  const handleCheckBox = (e) => {
    const val = e.target.value;
    if (selectedTags.includes(val)) {
      setSelectedTags(selectedTags.filter((id) => id !== val));
    } else {
      setSelectedTags([...selectedTags, val]);
    }
    setSelectAll(false);
  };

  const handleDialogClose = (option) => (data) => {
    setDialogState({ open: false });
    if (!data) return;
    if (!data.identifier) {
      const [element, key] = createSRAccessibilityTag({
        ...option,
        tag: data,
      });
      setValue(key, getCleanContent(element), {
        shouldDirty: true,
      });
    } else {
      const newObject = editScreenReaderTagContent(`${objectType}`)(
        getValues(`${objectType}`),
      )(data);
      setValue(`${objectType}`, newObject, {
        shouldDirty: true,
      });
    }
    closeActionsAnchorEl();
  };

  const handleMouseLeave = ({ identifier }) => {
    const elements = document.querySelectorAll(
      `[aria-describedby~="${identifier}"], [id="${identifier}"]`,
    );
    elements.forEach((element) => {
      if (!element?.textContent) {
        document
          .querySelector(`[id="${identifier}"]`)
          ?.classList.remove("active");
      } else {
        element?.classList.remove("active");
      }
    });
  };

  const handleMouseOver = ({ identifier }) => {
    const elements = document.querySelectorAll(
      `[aria-describedby="${identifier}"], [id="${identifier}"]`,
    );
    elements.forEach((element) => {
      if (!element?.textContent) {
        document.querySelector(`[id="${identifier}"]`)?.classList.add("active");
      } else {
        element?.classList.add("active");
      }
    });
  };

  const handleMouseUp = (e) => {
    if (e.button !== 0) return;
    const selection = window.getSelection();

    const range = _.pipe([getTrimmedRange, expandRangeToFindCommonNode])(
      selection.getRangeAt(0),
    );
    setContextMenuState({
      ...contextMenuState,
      mouseX: e.clientX,
      mouseY: e.clientY,
      options: getSROptions(contentElementRef.current)(range),
    });
  };

  const handleEditActionClick = useCallback(() => {
    const tag = srAccessibilityTags.find(
      (item) => item.identifier === currentId,
    );
    closeActionsAnchorEl();
    if (!tag) return;

    const selectedContents = Array.from(
      contentElement.querySelectorAll(`[aria-describedby="${tag.identifier}"]`),
    );

    setDialogState({
      open: true,
      option: {
        label: selectedContents.some((content) => content.tagName === "IMG")
          ? "Image"
          : null,
      },
      readonly,
      tag: {
        ...tag,
        content: tag?.content,
        selectedContent: selectedContents
          ?.map((content) => content.outerHTML)
          .join(""),
      },
    });
  }, [contentElement, currentId, readonly, srAccessibilityTags]);

  const openSRTagDialog = async ({ tag, ...option }) => {
    const { label, range, elements } = option;
    const [firstElement] = elements || [];
    let accessibleText;
    let content;
    let selectedContent;
    if (range)
      content = range.toString().replaceAll(/(?:&nbsp;|\u00A0)/gi, " ");
    if (elements) content = elements.map((el) => el.textContent).join(" ");
    switch (label) {
      case "MathML":
        const outerHTML = firstElement.outerHTML;
        accessibleText = await transformMathMl(client)(outerHTML);
        selectedContent = outerHTML;
        content = accessibleText;
        break;
      case "Image":
        const imageAssets = getValues(`${objectType}.imageAssets`);
        // Match on filename rather than the full src path: cloned passages keep
        // the original passage's asset path in the <img> src, so a full-path
        // compare misses and the Long Description comes up blank (CDE-17327).
        const imageFileName = getAssetFileName(elements[0]?.id);
        const image =
          imageFileName &&
          imageAssets.find((ia) => getAssetFileName(ia.src) === imageFileName);
        content = image?.longDescription || image?.altText || "";

        break;
      default:
        break;
    }

    closeContextMenu();
    setDialogState({
      open: true,
      option,
      tag: { ...tag, content, selectedContent },
    });
  };

  return (
    <>
      <Box
        onMouseUp={(e) => !readonly && handleMouseUp(e)}
        ref={contentElementRef}
        sx={{
          border: "1px solid lightgrey",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
          overflowY: "auto",
          paddingX: 1,
          width: "100%",
          "& .srAccessibilityElement.active, & .srAccessibilityElement.active *":
            {
              backgroundColor: "#ffff7b !important",
            },
          "& .srAccessibilityElement, & .srAccessibilityElement *": {
            backgroundColor: "lightgrey",
          },
          "& .interaction": {
            display: "none",
          },
          "& th.perspectiveTitle, & th.perspectiveSpacer,& th.perspective, & td.perspective, td.perspectiveSpacer":
            {
              textAlign: "left",
              verticalAlign: "middle",
              fontWeight: "normal",
              borderStyle: "solid",
              borderWidth: "1px",
              padding: "3px",
            },
          "& .qti-visually-hidden:not(.srNotInsertedText)": {
            display: "inline",
            backgroundColor: "lightgrey",
            "&::before": {
              content: '"["',
            },
            "&::after": {
              content: '"]"',
            },
            "&.active": {
              backgroundColor: "#ffff7b !important",
            },
          },
        }}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html:
              contentElement.querySelector(`#${objectType}-content`)
                .outerHTML || "",
          }}
        />
        {interactions.map(({ foils, identifier }, index) => (
          <List key={`${objectType}-interaction-${identifier}`}>
            {foils.map((_foil, i) => {
              const letter = getLetter(i);
              const selector = `${objectType}-interaction-${index}-foil-${i}-content`;
              const el = contentElement.querySelector(`#${selector}`);
              return (
                <ListItem key={selector}>
                  <ListItemAvatar>
                    <Avatar sx={{ height: 24, width: 24 }}>{letter}</Avatar>
                  </ListItemAvatar>
                  <ListItemText>
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: el?.outerHTML || "",
                      }}
                    />
                  </ListItemText>
                </ListItem>
              );
            })}
          </List>
        ))}
      </Box>
      {!!contextMenuState.options.length && (
        <Menu
          keepMounted
          open={contextMenuState.mouseY !== null}
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenuState.mouseX !== null && contextMenuState.mouseY !== null
              ? {
                  left: contextMenuState.mouseX,
                  top: contextMenuState.mouseY,
                }
              : undefined
          }
        >
          {contextMenuState.options.map((option) => (
            <MenuItem
              dense={true}
              key={option.label}
              onClick={() => openSRTagDialog(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}
      <Box
        sx={{
          display: srAccessibilityTags.length ? "flex" : "none",
          flexGrow: 1,
          height: "100%",
          maxHeight: orientation === "left-right" ? "100%" : 250,
          maxWidth: orientation === "left-right" ? 400 : "100%",
          minHeight: 250,
          minWidth: 400,
          overflowY: "auto",
        }}
      >
        <List
          dense={true}
          subheader={
            <ListSubheader
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {!readonly && (
                <ListItemIcon>
                  <Checkbox
                    checked={selectAll}
                    edge="start"
                    onChange={handleSelectAllCheckBox}
                    aria-label="Select All"
                  />
                </ListItemIcon>
              )}
              <Box>Screen Reader Tags</Box>
              <IconButton
                aria-label="more"
                aria-controls="sr-tags-bulk-actions-menu"
                aria-expanded={bulkActionsAnchorEl ? "true" : undefined}
                aria-haspopup="true"
                disabled={readonly}
                id="sr-tags-bulk-actions"
                onClick={(e) => setBulkActionsAnchorEl(e.target)}
              >
                <DotsVerticalIcon />
              </IconButton>
              <Menu
                anchorEl={bulkActionsAnchorEl}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "bottom",
                }}
                id="sr-tags-bulk-actions-menu"
                MenuListProps={{
                  "aria-labelledby": "sr-tags-bulk-actions",
                }}
                onClose={closeBulkActionsAnchorEl}
                open={Boolean(bulkActionsAnchorEl)}
                transformOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
              >
                <MenuItem onClick={deleteSelectedSRTags}>
                  <ListItemIcon>
                    <DeleteIcon size="small" />
                  </ListItemIcon>
                  <ListItemText>Delete Selected Tags</ListItemText>
                </MenuItem>
              </Menu>
            </ListSubheader>
          }
          sx={{
            backgroundColor: "background.paper",
            flexDirection: "column",
            flexGrow: 1,
            overflowX: "hidden",
            overflowY: "auto",
            paddingTop: 0,
            position: "relative",
          }}
        >
          {!!srAccessibilityTags &&
            srAccessibilityTags.map((tag, index) => {
              const elements = [
                ...contentElement.querySelectorAll(`[id="${tag.identifier}"]`),
              ];

              const getText = elements
                .map((element) => element.textContent)
                .join("");
              const text = !!tag?.tagName ? "Image" : getText;
              return (
                <ListItem
                  key={tag.identifier}
                  onMouseEnter={() => handleMouseOver(tag)}
                  onMouseLeave={() => handleMouseLeave(tag)}
                  sx={{ borderTop: "1px solid lightgrey" }}
                >
                  {!readonly && (
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedTags.includes(tag.identifier)}
                        edge="start"
                        onChange={handleCheckBox}
                        value={tag.identifier}
                      />
                    </ListItemIcon>
                  )}
                  <Tooltip aria-label={text} title={text}>
                    <ListItemText
                      disableTypography={true}
                      secondary={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{ lineHeight: "1.5em" }}
                            variant="body2"
                          >
                            {text}
                          </Typography>
                        </Box>
                      }
                    ></ListItemText>
                  </Tooltip>
                  <ListItemSecondaryAction
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <IconButton
                      aria-label="more"
                      aria-controls="sr-tag-actions-menu"
                      aria-expanded={actionsAnchorEl ? "true" : undefined}
                      aria-haspopup="true"
                      id={`sr-tag-${index}-actions`}
                      onClick={(e) => {
                        setCurrentId(tag.identifier);
                        setActionsAnchorEl(e.target);
                      }}
                      size="small"
                    >
                      <DotsVerticalIcon />
                    </IconButton>
                    <Menu
                      anchorEl={actionsAnchorEl}
                      anchorOrigin={{
                        horizontal: "right",
                        vertical: "bottom",
                      }}
                      id="sr-tag-actions-menu"
                      MenuListProps={{
                        "aria-labelledby": `sr-tag-${index}-actions`,
                      }}
                      onClose={closeActionsAnchorEl}
                      open={Boolean(actionsAnchorEl)}
                      transformOrigin={{
                        horizontal: "right",
                        vertical: "top",
                      }}
                    >
                      {!readonly && (
                        <MenuItem onClick={deleteSRTag}>
                          <ListItemIcon>
                            <DeleteIcon size="small" />
                          </ListItemIcon>
                          <ListItemText>Delete</ListItemText>
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleEditActionClick}>
                        <ListItemIcon>
                          <EditIcon />
                        </ListItemIcon>
                        <ListItemText>
                          {readonly ? "View" : "Edit"}
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
        </List>
      </Box>
      {dialogState.open && (
        <SRAccessibilityDialog
          handleClose={handleDialogClose}
          {...dialogState}
        />
      )}
    </>
  );
};

export { SRAccessibilityPanel };
